<script lang="ts">
import type { ColumnPinningState, ColumnSizingState, RowSelectionState, SortingState } from '@tanstack/vue-table';
import type { AnyItem, CompositePrimaryKey, GlobalFieldId } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import type { DataTableProps } from '#layers/ui';
import type { LayoutSortingState, SharedLayoutEmits, SharedLayoutProps } from '~/types/layout';
import {
	fieldIdToCollectionFieldPath,
	isCollectionFieldPathPermitted,
	resolveCollectionFieldPath,
	toCollectionFieldPathKey,
} from '#layers/data-model/utils/collection-field-path';

export interface TableDisplayOptions {
	visibleColumnPaths?: CollectionFieldPath[];
	/** Use visibleColumnPaths instead. Kept for backwards compatibility migration. */
	visibleColumnIds?: GlobalFieldId[];
	columnOrder?: string[];
	columnSizing?: ColumnSizingState;
	columnPinning?: ColumnPinningState;
	sorting?: LayoutSortingState;
	pageSize?: number;
}

export interface LayoutTableProps<T> extends SharedLayoutProps<TableDisplayOptions>, DataTableProps<T> {}
</script>

<script setup lang="ts" generic="T extends AnyItem = AnyItem">
import { createColumnHelper } from '@tanstack/vue-table';
import { reactiveOmit } from '@vueuse/core';
import { computed, h, watch } from 'vue';
import { virtualizeColumnAutosizeOnScrollFlag } from '#layers/base';
import { pickByPrimitiveFields } from '#layers/data-model/utils/item';
import { getItemPrimaryKey, getItemPrimaryKeyString, isEmptyPrimaryKey, parseItemPrimaryKeyString, stringifyPrimaryKey } from '#layers/data-model/utils/primary-key';
import { getCollectionPrimaryKeyFields } from '#layers/data-model/utils/schema/collection';
import { injectListingContext } from '~/components/Listing/Listing.vue';
import { exposeListingQueryToBlockRootContext } from '~/composables/block-context';
import { useConvertLayoutSortingStateToQuerySort } from '~/composables/use-convert-layout-sorting-state-to-query-sort';
import { useDeferredTrue } from '~/composables/use-deferred-true';
import { useItems } from '~/composables/use-items';
import { useSelection } from '~/composables/use-selection';
import { DEFAULT_LISTING_PAGE_SIZE } from '~/constants/pagination';
import { usePermissionsStore } from '~/stores/permissions';
import AddFieldAction from './AddFieldAction.vue';
import { getStableFilterKey } from './getStableFilterKey';
import LayoutTableDisplayOptions, { initializeLayoutTableOptions } from './LayoutTableDisplayOptions.vue';
import { useTableColumns } from './use-table-columns';
import { useTableQueryFields } from './use-table-query-fields';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps<LayoutTableProps<T>>();

const emit = defineEmits<SharedLayoutEmits>();

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });

const permissionsStore = usePermissionsStore();
const collectionPrimaryKeyFields = computed(() => props.collection ? getCollectionPrimaryKeyFields(props.collection) : []);
const displayOptions = computed(() => initializeLayoutTableOptions({ options: props.displayOptions, collection: props.collection }));

const configuredColumnPaths = computed(() =>
	displayOptions.value.visibleColumnPaths
	?? displayOptions.value.visibleColumnIds?.map((fieldId) => fieldIdToCollectionFieldPath(fieldId))
	?? [],
);

const visibleColumnPaths = computed(() =>
	configuredColumnPaths.value.filter((fieldPath) =>
		isCollectionFieldPathPermitted(props.collection, fieldPath, (collection, field) =>
			permissionsStore.canReadField(collection.id, field)),
	),
);

const visibleColumnKeys = computed(() => visibleColumnPaths.value.map((fieldPath) => toCollectionFieldPathKey(fieldPath)));

const pageSize = computed({
	get: () => displayOptions.value.pageSize ?? DEFAULT_LISTING_PAGE_SIZE,
	set: (value) => handleDisplayOptionUpdate({ pageSize: value }),
});

const { convert: convertSort } = useConvertLayoutSortingStateToQuerySort();

const visibleColumnQueryFields = useTableQueryFields(() => props.collection, visibleColumnPaths);

const queryFields = computed(() =>
	visibleColumnPaths.value.length === 0
		? collectionPrimaryKeyFields.value.map((field) => field.apiName)
		: visibleColumnQueryFields.value,
);

const { items, allItems, pageNumber, totalItemsCount, filteredItemsCount, isSettled, showLoading, query: itemsQuery } = useItems({
	collection: () => props.collection,
	query: () => ({
		filter: props.filter,
		fields: queryFields.value,
		search: props.searchTerm,
		sort: convertSort(displayOptions.value.sorting),
	}),
	pageSize,
});

exposeListingQueryToBlockRootContext(() => ({
	collection: props.collection,
	query: itemsQuery.value,
}));

/**
 * Preserve the difference between "not loaded yet" and "loaded empty" for the
 * virtualized table's initial header sizing behavior.
 *
 * `undefined` hides headers during the first loading gap; `[]` means the query
 * resolved empty, so the shared table can size columns from header labels.
 */
const tableData = computed(() => showLoading.value && items.value.length === 0 ? undefined : items.value);

const showSpinner = useDeferredTrue(showLoading, 350);

const { clear: clearSelection } = useSelection({ selection });

watch(pageNumber, () => {
	clearSelection();
});

const tableProps = reactiveOmit(
	props,
	'collection',
	'displayOptions',
	'filter',
	'searchTerm',
	'selection' as keyof typeof props,
	'selectionMode',
	'emptyState',
);

function handleRowSelectionUpdate(rows: RowSelectionState) {
	selection.value = Object.keys(rows)
		.filter((id) => rows[id] === true)
		.map((id) => parseItemPrimaryKeyString(id, props.collection));
}

const rowSelectionState = computed<RowSelectionState>(() => {
	const state: RowSelectionState = {};

	for (const item of selection.value) {
		state[getItemPrimaryKeyString(item, props.collection)] = true;
	}

	return state;
});

function toQuerySortEntry(
	value: { id: string; desc: boolean },
	visibleColumnPaths: CollectionFieldPath[],
	collection: ResolvedMonospaceCollection,
): LayoutSortingState[number] | null {
	const fieldPath = visibleColumnPaths.find((path) => toCollectionFieldPathKey(path) === value.id);
	if (!fieldPath || fieldPath.length !== 1) return null;

	const segment = resolveCollectionFieldPath(collection, fieldPath)?.at(-1);
	// `CollectionFieldPathField` is a discriminated union — the primitive variant has `type`, the
	// relation variant does not — so this narrowing also rejects single-segment relation columns.
	if (!segment || !('type' in segment.field)) return null;

	return { fieldId: segment.field.id, direction: value.desc ? 'desc' : 'asc' };
}

const sortingState = computed<SortingState>({
	get: () => displayOptions.value.sorting?.map(({ fieldId, direction }) => ({ id: fieldId, desc: direction === 'desc' })) ?? [],
	set: (value) =>
		handleDisplayOptionUpdate({
			sorting: value.flatMap((entry) => {
				const sortEntry = toQuerySortEntry(entry, visibleColumnPaths.value, props.collection);
				return sortEntry ? [sortEntry] : [];
			}),
		}),
});

function getRowId(row: T) {
	return stringifyPrimaryKey(pickByPrimitiveFields(row as AnyItem, collectionPrimaryKeyFields.value));
}

const columnVisibility = computed({
	get: () => {
		const visibility: Record<string, boolean> = {};

		for (const fieldPath of visibleColumnPaths.value) {
			visibility[toCollectionFieldPathKey(fieldPath)] = true;
		}

		return visibility;
	},
	set: (value) => {
		const nextVisibleColumnPaths = visibleColumnPaths.value.filter((fieldPath) => value[toCollectionFieldPathKey(fieldPath)] !== false);

		handleDisplayOptionUpdate({ visibleColumnPaths: nextVisibleColumnPaths });
	},
});

const listingContext = injectListingContext();
listingContext.setDisplayOptionsComponent(LayoutTableDisplayOptions);

const columnHelper = createColumnHelper<T>();

const { fieldColumns } = useTableColumns<T>({
	collection: () => props.collection,
	visibleColumnPaths,
});

const columns = computed(() => [
	...fieldColumns.value,
	columnHelper.display({
		id: '$add-field',
		enableResizing: false,
		size: 72,
		meta: {
			class: {
				th: 'justify-end',
			},
		},
		header: () => h(AddFieldAction, {
			collectionId: props.collection.id,
			visibleColumnPaths: visibleColumnPaths.value,
			onSelect: (fieldPath) => {
				handleDisplayOptionUpdate({
					visibleColumnPaths: [...visibleColumnPaths.value, fieldPath],
				});
			},
		}),
	}),
]);

const filterKey = computed(() => getStableFilterKey(props.filter));

function handleClick(row: { original: T }) {
	const rowId = getItemPrimaryKey(row.original as AnyItem, props.collection);

	if (isEmptyPrimaryKey(rowId)) {
		return;
	}

	if (props.selectionMode || selection.value.length > 0) {
		const rowKey = stringifyPrimaryKey(rowId);
		const isSelected = selection.value.some((id) => stringifyPrimaryKey(id) === rowKey);
		const isMultiple = !props.selectionMode || props.selectionMode === 'multiple';

		if (isSelected) {
			selection.value = isMultiple
				? selection.value.filter((id) => stringifyPrimaryKey(id) !== rowKey)
				: [];
		}
		else {
			selection.value = isMultiple
				? [...selection.value, rowId]
				: [rowId];
		}

		return;
	}

	emit('open', rowId);
}

/**
 * Resets shared virtualized column floors only when the listing data set or
 * layout meaningfully changes, not while rows recycle during scroll.
 *
 * `props.filter` is serialized: the surrounding form pipeline frequently
 * rewraps the filter object without materially changing it, and including
 * the raw reference made every parent render re-trigger a relax/freeze pass.
 */
const stabilizeColumnSizesDeps = computed(() => [
	props.collection.id,
	visibleColumnKeys.value,
	displayOptions.value.columnOrder ?? [],
	pageNumber.value,
	props.searchTerm ?? '',
	filterKey.value,
	showLoading.value,
]);

const mergedProps = computed(() => ({
	...tableProps,
	getRowId,
	rowSelection: rowSelectionState.value,
	rowSelectionOptions: {
		enableMultiRowSelection: !props.selectionMode || props.selectionMode === 'multiple',
	},
	columnOrder: displayOptions.value.columnOrder,
	columnPinning: displayOptions.value.columnPinning,
	columnSizing: displayOptions.value.columnSizing,
	stabilizeColumnSizesDeps: stabilizeColumnSizesDeps.value,
	virtualizeAutoSizeOnScroll: virtualizeColumnAutosizeOnScrollFlag.value,
}));

function handleDisplayOptionUpdate(updates: Partial<TableDisplayOptions>) {
	// Spread the raw props rather than the canonicalized `displayOptions` computed so we do not
	// synthesize keys (sorting, visibleColumnPaths) that the parent never set. Strip the legacy
	// `visibleColumnIds` key inline so emitted payloads stay clean.
	const { visibleColumnIds: _legacy, ...rest } = props.displayOptions ?? {};
	emit('update:displayOptions', { ...rest, ...updates });
}
</script>

<template>
	<DDataTable
		v-bind="mergedProps"
		v-model:sorting="sortingState"
		v-model:column-visibility="columnVisibility"
		:bordered="false"
		:columns
		:on-select="isSettled ? handleClick : undefined"
		:data="tableData"
		:ui="{
			tr: 'h-10 [--data-table-row-height:40px]',
			td: 'py-0',
		}"
		can-select
		can-sort
		hide-pagination
		manual-sorting
		sticky
		:virtualize="{ minItemSize: 40 }"
		@update:row-selection="handleRowSelectionUpdate"
		@update:column-order="(columnOrder) => handleDisplayOptionUpdate({ columnOrder: columnOrder as string[] | undefined })"
		@update:column-pinning="(columnPinning) => handleDisplayOptionUpdate({ columnPinning })"
		@update:column-sizing="(columnSizing) => handleDisplayOptionUpdate({ columnSizing })"
	>
		<template #empty>
			<DSpinner v-if="showSpinner" class="size-6 text-muted" />
			<span v-else-if="showLoading" data-test-id="loading-placeholder" class="size-6" aria-hidden="true" />
			<LayoutDefaultEmptyState
				v-else-if="!showLoading"
				:total-items-count
				:filtered-items-count
				:empty-state
				@reset-filters="$emit('resetFilters')"
				@empty-state-action="$emit('emptyStateAction')"
			/>
		</template>

		<template #footer>
			<DPaginationBar
				v-model:page="pageNumber"
				v-model:page-size="pageSize"
				:current-items="allItems.length"
				:total-items="totalItemsCount"
				:loading="showLoading"
				class="relative z-1 -mt-px border-t"
			/>
		</template>
	</DDataTable>
</template>
